import React from "react";
import { Control, Controller, FieldError } from "react-hook-form";
import Select from "components/select/select";
import Input from "components/input";
import Icon from "components/icon";
import { mockAdditionalCostTypes } from "_services/modules/driverTripCalculator/mockData";
import { DriverTripCalculatorFormData } from "_interfaces/driverTripCalculator.interfaces";
import { useAppTranslation } from "locale/useAppTranslation";

interface AdditionalCostRowProps {
  segmentIndex: number;
  costIndex: number;
  control: Control<DriverTripCalculatorFormData>;
  onRemove: () => void;
  error?: {
    cost_type?: FieldError;
    amount?: FieldError;
  };
}

const AdditionalCostRow: React.FC<AdditionalCostRowProps> = ({
  segmentIndex,
  costIndex,
  control,
  onRemove,
  error,
}) => {
  const { t } = useAppTranslation("driverTripCalculator");

  const costTypeOptions = mockAdditionalCostTypes.map((type) => ({
    value: type.value,
    label: type.label,
  }));

  return (
    <div className="flex items-start gap-3">
      <div className="flex-1">
        <Controller
          name={`segments.${segmentIndex}.additional_costs.${costIndex}.cost_type`}
          control={control}
          render={({ field }) => (
            <Select
              {...field}
              options={costTypeOptions}
              value={field.value}
              onValueChange={field.onChange}
              placeholder={t("select-cost-type-placeholder")}
              error={error?.cost_type}
              searchable
              onCreateOption={(value) => {
                field.onChange(value);
                return { value, label: value };
              }}
              createOptionLabel={(value) => t("add-cost-type-label", { value })}
              data-testid={`select-dtc-cost-type-${segmentIndex}-${costIndex}`}
            />
          )}
        />
      </div>

      <div className="flex-1">
        <Controller
          name={`segments.${segmentIndex}.additional_costs.${costIndex}.amount`}
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              type="text"
              placeholder={t("enter-amount-placeholder")}
              enableCurrencyFormat
              error={error?.amount}
              data-testid={`input-dtc-cost-price-${segmentIndex}-${costIndex}`}
            />
          )}
        />
      </div>

      <button
        type="button"
        onClick={onRemove}
        className="mt-2 rounded-md p-2 text-error hover:bg-error-50 transition-colors"
        data-testid={`btn-dtc-remove-cost-${segmentIndex}-${costIndex}`}
      >
        <Icon name="trash" className="h-5 w-5" />
      </button>
    </div>
  );
};

export default AdditionalCostRow;
